import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getPersonIdFromToken, getSelectedAccountInfo } from '@ibk/shared-auth';
import { attemptTokenRefresh, config, selectAccessToken, selectSelectedAccountId, store, useAppSelector, } from '@ibk/shared-config';
import { BillingRealtimeClient, } from '../../realtime/BillingRealtimeClient';
const BillingRealtimeContext = createContext(null);
const BILLING_REALTIME_CLIENT_KEY = Symbol.for('ibk.cobrancas.billing-realtime-client');
function createTokenFingerprint(accessToken) {
    let hash = 2_166_136_261;
    for (let index = 0; index < accessToken.length; index += 1) {
        hash ^= accessToken.codePointAt(index) ?? 0;
        hash = Math.imul(hash, 16_777_619);
    }
    return (hash >>> 0).toString(36);
}
function getAccessTokenIdentity(accessToken) {
    const payloadSegment = accessToken.split('.')[1];
    if (!payloadSegment)
        return createTokenFingerprint(accessToken);
    try {
        const payload = JSON.parse(atob(payloadSegment.replaceAll('-', '+').replaceAll('_', '/')));
        const sessionId = payload.jti;
        const issuedAt = payload.iat;
        const expiresAt = payload.exp;
        if (typeof sessionId === 'string' && typeof issuedAt === 'number' && typeof expiresAt === 'number') {
            return `${sessionId}:${issuedAt}:${expiresAt}`;
        }
    }
    catch {
    }
    return createTokenFingerprint(accessToken);
}
function getBillingRealtimeClient() {
    const registry = globalThis;
    const existingClient = registry[BILLING_REALTIME_CLIENT_KEY];
    if (existingClient)
        return existingClient;
    const client = new BillingRealtimeClient({
        onUnauthorized: () => attemptTokenRefresh({ dispatch: store.dispatch, getState: store.getState }),
    });
    registry[BILLING_REALTIME_CLIENT_KEY] = client;
    return client;
}
const billingRealtimeClient = getBillingRealtimeClient();
function createRequestContext(accessToken, selectedAccountId) {
    const account = getSelectedAccountInfo();
    const entityId = account?.entityId;
    const partyId = account?.targetId;
    const entityType = account?.entityType;
    const personId = accessToken ? getPersonIdFromToken(accessToken) : null;
    if (!accessToken || !entityId || !partyId || !entityType)
        return null;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
        'party-id': partyId,
        'entity-id': entityId,
        'entity-type': entityType,
        ...(personId ? { 'person-id': personId } : {}),
    };
    return {
        url: `${config.apiZeztraBffUrl}/beta/notifications/stream`,
        headers,
        fingerprint: [
            selectedAccountId ?? partyId,
            partyId,
            entityId,
            entityType,
            getAccessTokenIdentity(accessToken),
        ].join(':'),
    };
}
export function BillingRealtimeProvider({ children }) {
    const accessToken = useAppSelector(selectAccessToken);
    const selectedAccountId = useAppSelector(selectSelectedAccountId);
    const [status, setStatus] = useState('unavailable');
    const requestContext = useMemo(() => createRequestContext(accessToken, selectedAccountId), [accessToken, selectedAccountId]);
    const requestContextRef = useRef(requestContext);
    requestContextRef.current = requestContext;
    useEffect(() => {
        billingRealtimeClient.setContext(requestContext);
    }, [requestContext]);
    useEffect(() => {
        const unsubscribe = billingRealtimeClient.subscribeStatus(setStatus);
        return () => {
            unsubscribe();
        };
    }, []);
    const subscribe = useCallback((listener) => {
        const currentRequestContext = requestContextRef.current;
        if (!currentRequestContext)
            return () => undefined;
        const unsubscribe = billingRealtimeClient.subscribe(currentRequestContext, listener);
        let isUnsubscribed = false;
        return () => {
            if (isUnsubscribed)
                return;
            isUnsubscribed = true;
            unsubscribe();
        };
    }, []);
    const retain = useCallback(() => {
        const currentRequestContext = requestContextRef.current;
        if (!currentRequestContext)
            return () => undefined;
        return billingRealtimeClient.retain(currentRequestContext);
    }, []);
    const value = useMemo(() => ({ status, subscribe, retain }), [status, subscribe, retain]);
    return _jsx(BillingRealtimeContext.Provider, { value: value, children: children });
}
export function useBillingRealtime() {
    const context = useContext(BillingRealtimeContext);
    if (!context)
        throw new Error('useBillingRealtime must be used within a BillingRealtimeProvider');
    return context;
}
export function useOptionalBillingRealtime() {
    return useContext(BillingRealtimeContext);
}
